import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ThemeService } from '@core/services/theme.service';
import {
  PAGE_TITLES,
  usePageTitleWithLangChange,
} from '@core/utils/page-title.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, Observable } from 'rxjs';
import { ChooseLanguageComponent } from '../../../../core/components/choose-language/choose-language.component';
import { NavigationComponent } from '../../../../core/components/navigation/navigation.component';
import { CustomTranslateLoader } from '../../../../core/i18n/custom-translate-loader';
import { AnswerModuleService } from '../../../../core/services/answer-module.service';
import { RedirectService } from '../../../../core/services/redirect.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { LayoutComponent } from '../../../../shared/components/layout/layout.component';
import { LanguageSlotDirective } from '../../../../shared/directives/language-slot.directive';
import { AnswerModuleResponse } from '../../../../shared/models/answer-module-response';
import { ChooseLanguage } from '../../../../shared/models/choose-language';
import { EventChooseLanguageComponent } from '../../../../shared/models/components/event-choose-language';
import { StartOverView } from '../../../../shared/models/views-models/start-over-view';
import { SafeHtmlPipe } from '../../../../shared/pipes/safe-html.pipe';
import {
  createLanguageObject,
  mapLanguageOptions,
  updateTextDirection,
} from '../../../../shared/utils/helper.util';
import {
  cleanupViewportHeight,
  setupViewportHeight,
} from '../../../../shared/utils/viewport.util';

@Component({
  selector: 'app-start-over',
  imports: [
    NavigationComponent,
    LayoutComponent,
    LanguageSlotDirective,
    FormsModule,
    TranslateModule,
    ChooseLanguageComponent,
    SafeHtmlPipe,
    ButtonComponent,
  ],
  templateUrl: './start-over.component.html',
  styleUrl: './start-over.component.scss',
})
export class StartOverComponent implements OnInit, OnDestroy {
  private setPageTitle = usePageTitleWithLangChange();

  constructor(
    private route: ActivatedRoute,
    private redirectService: RedirectService,
    private answerService: AnswerModuleService,
    private translateService: TranslateService,
    private translateLoader: CustomTranslateLoader,
    private themeService: ThemeService,
  ) {}
  private _response!: AnswerModuleResponse;

  @Input()
  set response(value: AnswerModuleResponse) {
    this._response = value;
    if (value.themeId) {
      this.themeService.loadTheme(value.themeId);
    }
    this.onResponseSet(value);
  }
  protected onResponseSet(value: AnswerModuleResponse): void {}

  get response(): AnswerModuleResponse {
    return this._response;
  }
  startOverView?: StartOverView;
  options: { value: string; label: string; src: string }[] = [];
  allowNavigation = false;
  chooseLanguageComponent?: EventChooseLanguageComponent;
  languageOptions: { value: string; label: string; src: string }[] = [];
  selectedLanguage: string = '';
  startOverUrl: string = '';
  continueUrl: string = '';
  private readonly destroyRef = inject(DestroyRef);

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return this.allowNavigation;
  }

  @HostListener('window:popstate')
  onPopState() {
    history.pushState(null, '');
  }

  ngOnInit(): void {
    this.setPageTitle(
      PAGE_TITLES.START_OVER.key,
      PAGE_TITLES.START_OVER.fallback,
    );

    setupViewportHeight();

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const response = history.state.data as AnswerModuleResponse;
        if (response && response.view.type === 'START_OVER') {
          this.response = response;
          this.startOverView = response.view;
          this.startOverUrl = response.view.data.startOverUrl;
          this.continueUrl = response.view.data.continueUrl;
          this.chooseLanguageComponent =
            this.startOverView?.data?.components?.find(
              (i) => i.$type === '@event-choose-language',
            );
          if (this.chooseLanguageComponent) {
            this.languageOptions = mapLanguageOptions(
              this.chooseLanguageComponent,
            );
          } else {
            this.selectedLanguage = this.response.language;
            this.translateLoader
              .getTranslation(this.selectedLanguage, ['startover.'], this.response.fallbackLanguage)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(() => {
                  this.allowNavigation = true;
                  return [];
                }),
              )
              .subscribe((translation) => {
                this.translateService.setTranslation(
                  this.selectedLanguage,
                  translation,
                  true,
                );
                this.translateService.use(this.selectedLanguage);
              });
          }
        }
      });

    updateTextDirection(this.selectedLanguage);
  }

  ngOnDestroy(): void {
    cleanupViewportHeight();
  }

  setLanguage({ language, calledWithInitialLanguage }: ChooseLanguage) {
    this.selectedLanguage = language;

    updateTextDirection(language);
    if (!calledWithInitialLanguage) {
      this.postLanguage();
    }
  }

  postLanguage() {
    if (this.response?.responseId) {
      const languageRequest = createLanguageObject(
        this.response?.responseId,
        this.selectedLanguage,
      );
      this.answerService
        .postLanguage(
          this.response.responseId,
          this.response.stateId,
          languageRequest,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((newResponse) => {
          if (newResponse?.body) {
            this.response = newResponse.body;
          }
        });
    }
  }

  startOver(url: string) {
    if (this.response) {
      this.answerService
        .startOver(url)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe((res) => {
          if (res.body) {
            this.allowNavigation = true;
            this.redirectService.redirectToView(res.body);
          }
        });
    }
  }
}
